import { isBeforeDate } from '@shared/helpers/date.helpers';
import type { AiToolCall } from '@shared/models/ai-chat.model';

/**
 * Records the saved project `updated_at` on the last successful tool result.
 * The open client reads {@link latestAiProjectUpdatedAt} when the
 * `project_update` socket never arrives, so the next save does not keep a
 * stale optimistic-lock token.
 */
export const stampProjectUpdatedAt = (
  toolCalls: readonly AiToolCall[],
  projectUpdatedAt: string,
): AiToolCall[] => {
  const index = lastSuccessfulToolCallIndex(toolCalls);
  if (index < 0) return [...toolCalls];

  return toolCalls.map((call, i) => {
    if (i !== index || !call.result) return call;
    return {
      ...call,
      result: { ...call.result, projectUpdatedAt },
    };
  });
};

/**
 * Newest persisted project `updated_at` stamped on this thread, if a turn saved the project.
 */
export const latestAiProjectUpdatedAt = (
  messages: readonly { toolCalls?: readonly AiToolCall[] | undefined }[],
): string | undefined => {
  let latest: string | undefined;

  for (const message of messages) {
    for (const call of message.toolCalls ?? []) {
      const stamp = call.result?.projectUpdatedAt;
      if (!stamp) continue;
      if (latest === undefined || isBeforeDate(latest, stamp)) latest = stamp;
    }
  }

  return latest;
};

const lastSuccessfulToolCallIndex = (
  toolCalls: readonly AiToolCall[],
): number => {
  for (let i = toolCalls.length - 1; i >= 0; i--) {
    if (toolCalls[i]?.result?.success) return i;
  }
  return -1;
};
